import React, { useState } from 'react';

const CHART_OPTIONS = [
  { key: 'monthly', label: 'Hours by month' },
  { key: 'aircraft', label: 'Hours by aircraft type' },
  { key: 'airports', label: 'Most visited airports' },
  { key: 'capacity', label: 'Flying capacity split' },
];

const PIE_COLOURS = [
  '#2476a8',
  '#5c9f62',
  '#d28b34',
  '#8a6db0',
];

function formatMinutes(minutes, timeFormat) {
  const safeMinutes = Math.max(
    0,
    Math.round(Number(minutes) || 0)
  );

  if (timeFormat === 'hours-minutes') {
    const hours = Math.floor(safeMinutes / 60);
    const remainingMinutes = safeMinutes % 60;

    return `${hours}:${String(remainingMinutes).padStart(2, '0')}`;
  }

  return (safeMinutes / 60).toFixed(1);
}

function EmptyChart() {
  return (
    <div
      style={{
        padding: '1.2rem',
        textAlign: 'center',
        color: '#666666',
        fontSize: '0.8rem',
      }}
    >
      Not enough logbook information yet.
    </div>
  );
}

function HorizontalBarChart({
  data,
  valueKey,
  timeFormat,
  valueIsMinutes = true,
}) {
  if (!data || data.length === 0) {
    return <EmptyChart />;
  }

  const maximumValue = Math.max(
    1,
    ...data.map((entry) => Number(entry[valueKey]) || 0)
  );

  return (
    <div
      style={{
        display: 'grid',
        gap: '0.45rem',
      }}
    >
      {data.map((entry) => {
        const value = Number(entry[valueKey]) || 0;
        const width = Math.max(
          value > 0 ? 3 : 0,
          (value / maximumValue) * 100
        );

        return (
          <div
            key={entry.label}
            style={{
              display: 'grid',
              gridTemplateColumns: '125px 1fr 60px',
              alignItems: 'center',
              gap: '0.45rem',
            }}
          >
            <div
              title={entry.label}
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: '0.75rem',
                fontWeight: 600,
              }}
            >
              {entry.label}
            </div>

            <div
              style={{
                height: 18,
                background: '#e5e9ed',
                borderRadius: 4,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${width}%`,
                  height: '100%',
                  background: '#2476a8',
                  borderRadius: 4,
                }}
              />
            </div>

            <div
              style={{
                textAlign: 'right',
                fontSize: '0.73rem',
                fontWeight: 600,
              }}
            >
              {valueIsMinutes
                ? formatMinutes(value, timeFormat)
                : value}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function MonthlyChart({ data, timeFormat }) {
  if (!data || data.length === 0) {
    return <EmptyChart />;
  }

  const maximumMinutes = Math.max(
    1,
    ...data.map((entry) => Number(entry.minutes) || 0)
  );

  return (
    <div
      style={{
        height: 155,
        display: 'flex',
        alignItems: 'stretch',
        gap: '0.35rem',
        paddingTop: '0.4rem',
      }}
    >
      {data.map((entry) => {
        const minutes = Number(entry.minutes) || 0;
        const height = Math.max(
          minutes > 0 ? 4 : 0,
          (minutes / maximumMinutes) * 95
        );

        return (
          <div
            key={entry.label}
            style={{
              flex: '1 1 0',
              minWidth: 38,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
              alignItems: 'center',
            }}
          >
            <div
              style={{
                marginBottom: '0.2rem',
                fontSize: '0.67rem',
                fontWeight: 600,
              }}
            >
              {formatMinutes(minutes, timeFormat)}
            </div>

            <div
              title={`${entry.label}: ${formatMinutes(
                minutes,
                timeFormat
              )}`}
              style={{
                width: '70%',
                maxWidth: 48,
                height,
                background: '#2476a8',
                borderRadius: '4px 4px 0 0',
              }}
            />

            <div
              style={{
                width: '100%',
                paddingTop: '0.3rem',
                borderTop: '1px solid #9aa2aa',
                textAlign: 'center',
                fontSize: '0.66rem',
                whiteSpace: 'nowrap',
              }}
            >
              {entry.label}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CapacityPieChart({ data, timeFormat }) {
  const validData = (data || []).filter(
    (entry) => Number(entry.minutes) > 0
  );

  const totalMinutes = validData.reduce(
    (total, entry) => total + Number(entry.minutes),
    0
  );

  if (totalMinutes <= 0) {
    return <EmptyChart />;
  }

  let runningDegrees = 0;

  const segments = validData.map((entry, index) => {
    const segmentDegrees =
      (Number(entry.minutes) / totalMinutes) * 360;

    const startDegrees = runningDegrees;
    const endDegrees = runningDegrees + segmentDegrees;

    runningDegrees = endDegrees;

    return {
      ...entry,
      colour: PIE_COLOURS[index % PIE_COLOURS.length],
      startDegrees,
      endDegrees,
    };
  });

  const gradient = segments
    .map(
      (segment) =>
        `${segment.colour} ${segment.startDegrees}deg ${segment.endDegrees}deg`
    )
    .join(', ');

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '1.2rem',
        flexWrap: 'wrap',
        minHeight: 145,
      }}
    >
      <div
        role="img"
        aria-label="Flying capacity split pie chart"
        style={{
          width: 112,
          height: 112,
          borderRadius: '50%',
          background: `conic-gradient(${gradient})`,
          border: '1px solid #a2a8ae',
        }}
      />

      <div
        style={{
          display: 'grid',
          gap: '0.45rem',
          minWidth: 180,
        }}
      >
        {segments.map((segment) => (
          <div
            key={segment.label}
            style={{
              display: 'grid',
              gridTemplateColumns: '13px 1fr auto',
              alignItems: 'center',
              gap: '0.4rem',
              fontSize: '0.76rem',
            }}
          >
            <span
              style={{
                width: 11,
                height: 11,
                borderRadius: 2,
                background: segment.colour,
              }}
            />

            <span>{segment.label}</span>

            <strong>
              {formatMinutes(segment.minutes, timeFormat)}
            </strong>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function LogbookStatisticsCharts({
  summaryCards = [],
  monthlyData = [],
  aircraftData = [],
  airportData = [],
  capacityData = [],
  timeFormat = 'decimal-hours',
}) {
  const [selectedChart, setSelectedChart] =
    useState('monthly');

  return (
    <div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns:
            'repeat(auto-fit, minmax(135px, 1fr))',
          gap: '0.45rem',
          marginBottom: '0.65rem',
        }}
      >
        {summaryCards.map((card) => (
          <div
            key={card.label}
            style={{
              padding: '0.5rem',
              background: '#ffffff',
              border: '1px solid #c6cbd0',
              borderRadius: 5,
            }}
          >
            <div
              style={{
                fontSize: '0.69rem',
                color: '#555555',
              }}
            >
              {card.label}
            </div>

            <div
              style={{
                marginTop: '0.12rem',
                fontSize: '1.05rem',
                fontWeight: 700,
              }}
            >
              {card.value}
            </div>
          </div>
        ))}
      </div>

      <div
        style={{
          padding: '0.6rem',
          background: '#ffffff',
          border: '1px solid #c6cbd0',
          borderRadius: 5,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '0.5rem',
            marginBottom: '0.65rem',
            flexWrap: 'wrap',
          }}
        >
          <strong
            style={{
              fontSize: '0.82rem',
            }}
          >
            Logbook chart
          </strong>

          <select
            value={selectedChart}
            onChange={(event) =>
              setSelectedChart(event.target.value)
            }
            style={{
              padding: '0.3rem 0.45rem',
              border: '1px solid #858c92',
              borderRadius: 4,
              background: '#ffffff',
              fontSize: '0.76rem',
            }}
          >
            {CHART_OPTIONS.map((option) => (
              <option
                key={option.key}
                value={option.key}
              >
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {selectedChart === 'monthly' ? (
          <MonthlyChart
            data={monthlyData}
            timeFormat={timeFormat}
          />
        ) : null}

        {selectedChart === 'aircraft' ? (
          <HorizontalBarChart
            data={aircraftData}
            valueKey="minutes"
            timeFormat={timeFormat}
          />
        ) : null}

        {selectedChart === 'airports' ? (
          <HorizontalBarChart
            data={airportData}
            valueKey="visits"
            timeFormat={timeFormat}
            valueIsMinutes={false}
          />
        ) : null}

        {selectedChart === 'capacity' ? (
          <CapacityPieChart
            data={capacityData}
            timeFormat={timeFormat}
          />
        ) : null}
      </div>
    </div>
  );
}